/**
 * /v1/users/* — 登入 / 登出
 *
 * 路徑集中在檔頭的 maps 變量；baseURL 走 http.ts 預設（VITE_AUTH_API_URL）。
 *
 * 注意：
 * - 登入端點不需要 token，所以 skipAuth: true（避免 Pinia 還沒初始化時嘗試讀 store）
 * - logout 預設帶 token（useHttp 會自動從 authStore 取）
 * - 不再支援讀取 Response Header 內的 Authorization token；新後端應把 token 放 body
 *   舊行為仍保留在 src/authService.ts，未動原檔
 */
import { useHttp, ApiError } from '../http'
import type {
  DevLoginBody,
  DevLoginResponse,
  LoginWithTicketParams,
  LoginWithTicketBody,
  LoginWithTicketResponse,
  GuestLoginResponse,
  LoginResult,
} from './types'

const maps = {
  devLogin: '/v1/users/dev/login',
  loginWithTicket: (ticket: string): string => `/v1/users/login/${encodeURIComponent(ticket)}`,
  guestLogin: '/v1/users/guest/login',
  logout: '/v1/users/logout',
} as const

/**
 * 訪客登入（demo 試玩）：免 ticket / 免 body，直接換一位全新試玩玩家的 token。
 * ⚠️ 每次呼叫 = 一位新玩家（獨立餘額）；token 只存記憶體（authStore），不落 storage。
 *
 * @throws ApiError 登入失敗 / 缺 token
 */
export const guestLogin = async (): Promise<LoginResult> => {
  const data = await useHttp.post<GuestLoginResponse>(maps.guestLogin, undefined, {
    skipAuth: true,
  })
  const token = data?.token
  if (!token) {
    throw new ApiError({ status: 200, statusCode: -1, msg: '登录响应缺少 token' })
  }
  return { token, userId: data?.user_id ?? 0 }
}

/**
 * dev acc 登入：把 URL 上的 acc / operator_id 等參數送出換 token
 *
 * @param loginParams 從 parseLoginParams() 拿到的 key-value，至少需要 acc（URL `?acc=` 空值時已自動生成）
 * @throws ApiError 登入失敗 / token 缺
 */
export const fetchToken = async (loginParams: Record<string, string>): Promise<LoginResult> => {
  const reqBody: DevLoginBody = {
    account: loginParams['acc'] ?? '',
  }
  for (const [key, value] of Object.entries(loginParams)) {
    if (key === 'acc' || value === undefined) continue
    if (key === 'operator_id') {
      const n = Number(value)
      reqBody[key] = Number.isFinite(n) ? n : value
    } else {
      reqBody[key] = value
    }
  }

  const data = await useHttp.post<DevLoginResponse>(maps.devLogin, reqBody, {
    skipAuth: true,
  })

  const token = data?.token || data?.access_token
  if (!token) {
    throw new ApiError({
      status: 200,
      statusCode: -1,
      msg: '登录响应缺少 token',
    })
  }

  return {
    token,
    userId: data?.user_id ?? 0,
  }
}

/**
 * ticket 登入：用一次性 ticket（由營運商後台下發）換 session token
 *
 * @param params { ticket, gameCode } — 見 LoginWithTicketParams
 * @throws ApiError ticket 失效 / 缺 token
 */
export const loginWithTicket = async ({
  ticket,
  gameCode,
}: LoginWithTicketParams): Promise<LoginResult> => {
  const body: LoginWithTicketBody = { game_code: gameCode }
  const data = await useHttp.post<LoginWithTicketResponse>(maps.loginWithTicket(ticket), body, {
    skipAuth: true,
  })

  const token = data?.token
  if (!token) {
    throw new ApiError({
      status: 200,
      statusCode: -1,
      msg: '登录响应缺少 token',
    })
  }

  return {
    token,
    userId: data?.user_id ?? 0,
  }
}

/**
 * 登出：通知後端清 session
 *
 * - useHttp 會自動帶 Authorization（從 authStore）
 * - 失敗靜默處理，不影響前端登出 UI 流程
 */
export const logout = async (): Promise<void> => {
  try {
    await useHttp.post<unknown>(maps.logout)
  } catch {
    // 靜默失敗：登出動作以前端 UI 切換為準
  }
}
